import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

// ── Date preset helpers ───────────────────────────────────────
const getDateRange = (preset, customFrom, customTo) => {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  const endOf   = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);

  switch (preset) {
    case 'today':
      return { from: startOf(today), to: endOf(today) };

    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { from: startOf(y), to: endOf(y) };
    }

    case 'this_month':
      return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: endOf(today) };

    case 'last_month': {
      const lmStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lmEnd   = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: startOf(lmStart), to: endOf(lmEnd) };
    }

    case 'last_30': {
      const l30 = new Date(today); l30.setDate(l30.getDate() - 30);
      return { from: startOf(l30), to: endOf(today) };
    }

    case 'this_fy': {
      // India FY: April 1 to March 31
      const fyStart = today.getMonth() >= 3
        ? new Date(today.getFullYear(), 3, 1)
        : new Date(today.getFullYear() - 1, 3, 1);
      return { from: startOf(fyStart), to: endOf(today) };
    }

    case 'custom':
      return {
        from: customFrom ? new Date(customFrom + 'T00:00:00') : null,
        to:   customTo   ? new Date(customTo   + 'T23:59:59') : null,
      };

    case 'till_date':
    default:
      return { from: null, to: null };
  }
};

const formatRangeLabel = (preset, customFrom, customTo) => {
  const fmt = (d) => d?.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) || '';
  const { from, to } = getDateRange(preset, customFrom, customTo);
  if (!from && !to) return 'All Time';
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  return 'All Time';
};

const PRESETS = [
  { key: 'this_fy',    label: 'This FY' },
  { key: 'till_date',  label: 'Till Date' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_30',    label: 'Last 30 Days' },
  { key: 'today',      label: 'Today' },
  { key: 'yesterday',  label: 'Yesterday' },
  { key: 'custom',     label: 'Custom Range' },
];

const PIPELINE_ORDER = [
  'pending approval','approved','do generated',
  'loaded','delivered','completed','cancelled','rejected',
];

const PIPELINE_COLORS = {
  'pending approval': { bg: '#fef3c7', color: '#92400e' },
  'pending':          { bg: '#fef3c7', color: '#92400e' },
  'approved':         { bg: '#dbeafe', color: '#1d4ed8' },
  'do generated':     { bg: '#ede9fe', color: '#7c3aed' },
  'loaded':           { bg: '#fce7f3', color: '#9d174d' },
  'delivered':        { bg: '#d1fae5', color: '#065f46' },
  'completed':        { bg: '#d1fae5', color: '#065f46' },
  'cancelled':        { bg: '#fee2e2', color: '#991b1b' },
  'rejected':         { bg: '#fee2e2', color: '#991b1b' },
};

// ── Component ─────────────────────────────────────────────────
const MonitorDashboard = () => {
  const navigate = useNavigate();

  const [orders,  setOrders]  = useState([]);
  const [quotas,  setQuotas]  = useState([]);
  const [buyers,  setBuyers]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');

  // Date filter state
  const [preset,     setPreset]     = useState('till_date');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [dropOpen,   setDropOpen]   = useState(false);
  const dropRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Role guard ────────────────────────────────────────────────
  useEffect(() => {
    const role = (localStorage.getItem('userRole') || '').toLowerCase();
    if (role !== 'monitor') { navigate('/login-authentication'); return; }
    loadAll();
  }, []);

  // ── Fetch ─────────────────────────────────────────────────────
  const loadAll = async () => {
    setLoading(true);
    await Promise.all([fetchOrders(), fetchQuotas(), fetchBuyers()]);
    setLoading(false);
  };

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('buyer_bookings')
        .select('readable_booking_id, buyer_email, factory_name, grade, quantity, rate_per_ton, total_amount, status, booking_date, created_at, tds_on_buyer_payment, tds_on_factory_payment')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setOrders(data || []);
    } catch (err) { console.error('fetchOrders error:', err); }
  };

  const fetchQuotas = async () => {
    try {
      const { data, error } = await supabase
        .from('quotas')
        .select('readable_offer_id, factory_name, grade, quantity, price_per_ton, availability_status, available_from_date, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setQuotas(data || []);
    } catch (err) { console.error('fetchQuotas error:', err); }
  };

  const fetchBuyers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('full_name, email, company_name, gstin, city, is_active, created_at')
        .eq('role', 'buyer')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setBuyers(data || []);
    } catch (err) { console.error('fetchBuyers error:', err); }
  };

  // ── Helpers ───────────────────────────────────────────────────
  const handleLogout = () => {
    ['userRole','userEmail','userName','userId','accessToken'].forEach(k => localStorage.removeItem(k));
    navigate('/login-authentication');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatAmt = (val) => {
    if (!val && val !== 0) return '—';
    const n = parseFloat(val);
    if (n >= 10000000) return '₹' + (n / 10000000).toFixed(2) + ' Cr';
    if (n >= 100000)   return '₹' + (n / 100000).toFixed(1) + ' L';
    return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const formatMT = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('en-IN', { maximumFractionDigits: 1 }) + ' MT';
  };

  const statusBadge = (status) => {
    const s = (status || '').toLowerCase().replace(/_/g, ' ');
    const map = {
      'pending approval': { bg: '#fef3c7', color: '#92400e' },
      'pending':          { bg: '#fef3c7', color: '#92400e' },
      'approved':         { bg: '#dbeafe', color: '#1d4ed8' },
      'do generated':     { bg: '#ede9fe', color: '#7c3aed' },
      'loaded':           { bg: '#fce7f3', color: '#9d174d' },
      'delivered':        { bg: '#d1fae5', color: '#065f46' },
      'completed':        { bg: '#d1fae5', color: '#065f46' },
      'cancelled':        { bg: '#fee2e2', color: '#991b1b' },
      'rejected':         { bg: '#fee2e2', color: '#991b1b' },
    };
    return map[s] || { bg: '#f3f4f6', color: '#374151' };
  };

  // ── Date-filtered slices ──────────────────────────────────────
  const { from: dateFrom, to: dateTo } = useMemo(
    () => getDateRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  const inRange = (dateStr) => {
    if (!dateFrom && !dateTo) return true;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return true;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo   && d > dateTo)   return false;
    return true;
  };

  const filteredOrders = useMemo(
    () => orders.filter(o => inRange(o.booking_date || o.created_at)),
    [orders, dateFrom, dateTo]
  );

  const filteredQuotas = useMemo(
    () => quotas.filter(q => inRange(q.created_at)),
    [quotas, dateFrom, dateTo]
  );

  const filteredBuyers = useMemo(
    () => buyers.filter(b => inRange(b.created_at)),
    [buyers, dateFrom, dateTo]
  );

  // ── Analytics (all from filtered data) ───────────────────────
  const analytics = useMemo(() => {
    const todayStr = new Date().toISOString().split('T')[0];

    // Period snapshot — MT and Value from approved orders only (pending are unverified)
    const approvedStatuses = ['approved', 'do generated', 'do_generated', 'loaded', 'delivered'];
    const approvedOrders = filteredOrders.filter(o => approvedStatuses.includes((o.status || '').toLowerCase().replace(/_/g, ' ')));
    const periodMT    = approvedOrders.reduce((s, o) => s + (parseFloat(o.quantity)    || 0), 0);
    const periodValue = approvedOrders.reduce((s, o) => s + (parseFloat(o.rate_per_ton || 0) * parseFloat(o.quantity || 0)), 0);
    const tdsReceivable = approvedOrders.reduce((s, o) => s + (parseFloat(o.tds_on_buyer_payment) || 0), 0);
    const tdsPayable    = approvedOrders.reduce((s, o) => s + (parseFloat(o.tds_on_factory_payment) || 0), 0);
    const pendingCount = filteredOrders.filter(o => (o.status || '').toLowerCase().includes('pending')).length;

    // For "Today's" label — only when preset is today
    const isToday = preset === 'today';

    // Pipeline
    const pipeline = {};
    filteredOrders.forEach(o => {
      const s = (o.status || 'unknown').toLowerCase().replace(/_/g, ' ');
      if (!pipeline[s]) pipeline[s] = { count: 0, mt: 0, value: 0 };
      pipeline[s].count++;
      pipeline[s].mt    += parseFloat(o.quantity)    || 0;
      pipeline[s].value += (parseFloat(o.rate_per_ton || 0) * parseFloat(o.quantity || 0));
    });

    // Factory performance
    const factoryMap = {};
    filteredQuotas.forEach(q => {
      const f = q.factory_name || 'Unknown';
      if (!factoryMap[f]) factoryMap[f] = { listed: 0, booked: 0, value: 0 };
      factoryMap[f].listed += parseFloat(q.quantity) || 0;
    });
    filteredOrders.filter(o => approvedStatuses.includes((o.status || '').toLowerCase().replace(/_/g, ' '))).forEach(o => {
      const f = o.factory_name || 'Unknown';
      if (!factoryMap[f]) factoryMap[f] = { listed: 0, booked: 0, value: 0 };
      factoryMap[f].booked += parseFloat(o.quantity)    || 0;
      factoryMap[f].value  += parseFloat(o.rate_per_ton || 0) * parseFloat(o.quantity || 0);
    });
    const factoryPerf = Object.entries(factoryMap)
      .map(([name, d]) => ({
        name, listed: d.listed, booked: d.booked,
        remaining: Math.max(0, d.listed - d.booked),
        util: d.listed > 0 ? Math.round((d.booked / d.listed) * 100) : 0,
        value: d.value,
      }))
      .sort((a, b) => b.booked - a.booked);

    // Top 5 buyers — approved orders only
    const buyerMap = {};
    filteredOrders.filter(o => approvedStatuses.includes((o.status || '').toLowerCase().replace(/_/g, ' '))).forEach(o => {
      const e = o.buyer_email || 'unknown';
      if (!buyerMap[e]) buyerMap[e] = { email: e, mt: 0, value: 0, orders: 0 };
      buyerMap[e].mt     += parseFloat(o.quantity)    || 0;
      buyerMap[e].value  += parseFloat(o.rate_per_ton || 0) * parseFloat(o.quantity || 0);
      buyerMap[e].orders++;
    });
    const topBuyers = Object.values(buyerMap).sort((a, b) => b.mt - a.mt).slice(0, 5);

    // Quota health (always from full quota list — not date filtered)
    const sevenDaysAgo  = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const availQuotas   = quotas.filter(q => q.availability_status === 'available' && parseFloat(q.quantity) > 0);
    const totalAvailMT  = availQuotas.reduce((s, q) => s + (parseFloat(q.quantity) || 0), 0);
    const totalListedMT = quotas.reduce((s, q) => s + (parseFloat(q.quantity) || 0), 0);
    const slowMovers    = availQuotas.filter(q => (q.created_at || '') < sevenDaysAgo);
    const totalBookedMT = orders.filter(o => approvedStatuses.includes((o.status || '').toLowerCase().replace(/_/g, ' '))).reduce((s, o) => s + (parseFloat(o.quantity) || 0), 0);
    const soldPct = totalListedMT > 0 ? Math.round((totalBookedMT / totalListedMT) * 100) : 0;

    // Monthly revenue (approved orders only)
    const monthMap = {};
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    filteredOrders.filter(o => approvedStatuses.includes((o.status || '').toLowerCase().replace(/_/g, ' '))).forEach(o => {
      const d = new Date(o.booking_date || o.created_at);
      if (isNaN(d.getTime())) return;
      const key   = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      if (!monthMap[key]) monthMap[key] = { label, mt: 0, value: 0, count: 0 };
      monthMap[key].mt    += parseFloat(o.quantity)    || 0;
      monthMap[key].value += (parseFloat(o.rate_per_ton || 0) * parseFloat(o.quantity || 0));
      monthMap[key].count++;
    });
    const monthlyRevenue = Object.entries(monthMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([, v]) => v);

    return {
      periodOrders: filteredOrders.length,
      periodMT,
      periodValue,
      tdsReceivable,
      tdsPayable,
      pendingCount,
      isToday,
      pipeline,
      factoryPerf,
      topBuyers,
      totalAvailMT,
      totalBookedMT,
      totalListedMT,
      soldPct,
      slowMovers: slowMovers.length,
      monthlyRevenue,
      activeBuyers: buyers.filter(b => b.is_active).length,
      activeQuotas: availQuotas.length,
    };
  }, [filteredOrders, filteredQuotas, filteredBuyers, orders, quotas, buyers, preset]);

  const tabs = [
    { key: 'orders', label: 'Orders', icon: 'ShoppingCart', count: filteredOrders.length },
    { key: 'quotas', label: 'Quotas', icon: 'Package',      count: filteredQuotas.length },
    { key: 'buyers', label: 'Buyers', icon: 'Users',        count: filteredBuyers.length },
  ];

  const rangeLabel    = formatRangeLabel(preset, customFrom, customTo);
  const presetLabel   = PRESETS.find(p => p.key === preset)?.label || 'Till Date';
  const snapshotLabel = preset === 'today' ? "Today's" : 'Period';

  // ── Render ────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Monitor Dashboard - SugarHathi</title>
        <meta name="description" content="Director-level read-only platform view for SugarHathi monitors." />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#f8f1e9' }}>

        {/* ── Header ── */}
        <header className="bg-white border-b shadow-sm sticky top-0 z-40" style={{ borderColor: '#e5e7eb' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl overflow-hidden shadow-sm ring-1 ring-amber-200 cursor-pointer" onClick={() => navigate('/')} title="SugarHathi home">
                <img src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png" alt="SugarHathi" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-base font-bold leading-tight" style={{ color: '#1e4a72' }}>SugarHathi</h1>
                <p className="text-xs" style={{ color: '#6b7280' }}>Monitor Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#ede9fe', color: '#7c3aed' }}>
                <Icon name="Eye" size={13} />
                Monitor
              </span>
              <span className="hidden sm:block text-sm font-medium" style={{ color: '#374151' }}>
                {localStorage.getItem('userName') || ''}
              </span>
              <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition hover:bg-gray-50" style={{ borderColor: '#e5e7eb', color: '#6b7280' }}>
                <Icon name="LogOut" size={14} />
                Logout
              </button>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center min-h-96">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm" style={{ color: '#9ca3af' }}>Loading dashboard...</p>
            </div>
          </div>
        ) : (
          <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

            {/* ── Date Filter ── */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs font-medium" style={{ color: '#6b7280' }}>Showing data for</p>
                <p className="text-sm font-bold" style={{ color: '#1f2937' }}>{rangeLabel}</p>
              </div>
              <div className="relative" ref={dropRef}>
                <button
                  onClick={() => setDropOpen(v => !v)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-white text-sm font-semibold shadow-sm transition hover:shadow-md"
                  style={{ borderColor: '#1e4a72', color: '#1e4a72' }}
                >
                  <Icon name="Calendar" size={15} />
                  {presetLabel}
                  <Icon name={dropOpen ? 'ChevronUp' : 'ChevronDown'} size={14} />
                </button>

                {dropOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border z-50 overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                    {PRESETS.map(p => (
                      <button
                        key={p.key}
                        onClick={() => { setPreset(p.key); if (p.key !== 'custom') setDropOpen(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm transition hover:bg-amber-50"
                        style={{
                          backgroundColor: preset === p.key ? '#1e4a72' : 'transparent',
                          color:           preset === p.key ? '#fff'    : '#374151',
                          fontWeight:      preset === p.key ? '600'     : '400',
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                    {preset === 'custom' && (
                      <div className="px-4 pb-3 pt-1 space-y-2 border-t" style={{ borderColor: '#f3f4f6' }}>
                        <div>
                          <label className="text-xs font-medium block mb-1" style={{ color: '#6b7280' }}>From</label>
                          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                            className="w-full text-xs px-2 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-amber-400"
                            style={{ borderColor: '#d1d5db' }} />
                        </div>
                        <div>
                          <label className="text-xs font-medium block mb-1" style={{ color: '#6b7280' }}>To</label>
                          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                            className="w-full text-xs px-2 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-amber-400"
                            style={{ borderColor: '#d1d5db' }} />
                        </div>
                        <button onClick={() => setDropOpen(false)}
                          className="w-full py-1.5 rounded-lg text-xs font-semibold text-white"
                          style={{ backgroundColor: '#1e4a72' }}>
                          Apply
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Section 1: Period Snapshot ── */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Icon name={preset === 'today' ? 'Sun' : 'Calendar'} size={16} color="#f59e0b" />
                <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>
                  {snapshotLabel} Snapshot
                  {preset === 'today' && (
                    <span className="ml-2 text-xs font-normal" style={{ color: '#6b7280' }}>
                      {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: `${snapshotLabel} Orders`,    value: analytics.periodOrders,         icon: 'ShoppingCart', color: '#1e4a72', bg: '#dbeafe' },
                  { label: `${snapshotLabel} MT Booked`, value: formatMT(analytics.periodMT),   icon: 'Package',      color: '#92400e', bg: '#fef3c7' },
                  { label: `${snapshotLabel} Value`,     value: formatAmt(analytics.periodValue),icon: 'DollarSign',  color: '#065f46', bg: '#d1fae5' },
                  { label: 'Pending Approval',           value: analytics.pendingCount,          icon: 'Clock',        color: '#dc2626', bg: '#fee2e2' },
                ].map(card => (
                  <div key={card.label} className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: card.bg }}>
                      <Icon name={card.icon} size={20} color={card.color} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: '#1f2937' }}>{card.value}</p>
                    <p className="text-xs mt-1 font-medium" style={{ color: '#6b7280' }}>{card.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Section 2: Platform Summary ── */}
            <section>
              <h2 className="text-base font-bold mb-3" style={{ color: '#1f2937' }}>Platform Summary <span className="text-xs font-normal" style={{ color: '#9ca3af' }}>(All Time)</span></h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Orders',  value: orders.length,                                  icon: 'ShoppingCart', color: '#1e4a72', bg: '#dbeafe' },
                  { label: 'Total MT Sold', value: formatMT(orders.reduce((s,o) => s + (parseFloat(o.quantity) || 0), 0)), icon: 'Package', color: '#92400e', bg: '#fef3c7' },
                  { label: 'Active Buyers', value: analytics.activeBuyers,                         icon: 'Users',        color: '#065f46', bg: '#d1fae5' },
                  { label: 'Live Quotas',   value: analytics.activeQuotas,                         icon: 'BarChart2',    color: '#7c3aed', bg: '#ede9fe' },
                ].map(card => (
                  <div key={card.label} className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: card.bg }}>
                      <Icon name={card.icon} size={20} color={card.color} />
                    </div>
                    <p className="text-2xl font-bold" style={{ color: '#1f2937' }}>{card.value}</p>
                    <p className="text-xs mt-1 font-medium" style={{ color: '#6b7280' }}>{card.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ── TDS Summary ── */}
            <section>
              <h2 className="text-base font-bold mb-3" style={{ color: '#1f2937' }}>
                TDS Summary
                <span className="text-xs font-normal ml-2" style={{ color: '#9ca3af' }}>(Approved Orders — Period)</span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* TDS Receivable */}
                <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#d1fae5', borderLeftWidth: 4 }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#065f46' }}>TDS Receivable</p>
                      <p className="text-2xl font-bold" style={{ color: '#065f46' }}>{formatAmt(analytics.tdsReceivable)}</p>
                      <p className="text-xs mt-2" style={{ color: '#6b7280' }}>Buyers declared u/s 194Q — credited to VFPL 26AS</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
                      <Icon name="TrendingUp" size={20} color="#065f46" />
                    </div>
                  </div>
                </div>
                {/* TDS Payable */}
                <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: '#fee2e2', borderLeftWidth: 4 }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#dc2626' }}>TDS Payable</p>
                      <p className="text-2xl font-bold" style={{ color: '#dc2626' }}>{formatAmt(analytics.tdsPayable)}</p>
                      <p className="text-xs mt-2" style={{ color: '#6b7280' }}>VFPL deducted from factory u/s 194Q — payable to govt</p>
                    </div>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#fee2e2' }}>
                      <Icon name="AlertCircle" size={20} color="#dc2626" />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Section 3: Order Pipeline ── */}
            <section>
              <h2 className="text-base font-bold mb-3" style={{ color: '#1f2937' }}>Order Pipeline <span className="text-xs font-normal" style={{ color: '#9ca3af' }}>({presetLabel})</span></h2>
              {Object.keys(analytics.pipeline).length === 0 ? (
                <div className="bg-white rounded-2xl border shadow-sm p-8 text-center" style={{ borderColor: '#e5e7eb' }}>
                  <p className="text-sm" style={{ color: '#9ca3af' }}>No orders in selected period</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {PIPELINE_ORDER
                    .filter(s => analytics.pipeline[s])
                    .map(s => {
                      const d  = analytics.pipeline[s];
                      const pc = PIPELINE_COLORS[s] || { bg: '#f3f4f6', color: '#374151' };
                      return (
                        <div key={s} className="bg-white rounded-xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize mb-2 inline-block" style={{ backgroundColor: pc.bg, color: pc.color }}>{s}</span>
                          <p className="text-xl font-bold" style={{ color: '#1f2937' }}>{d.count}</p>
                          <p className="text-xs font-medium mt-0.5" style={{ color: '#6b7280' }}>{formatMT(d.mt)}</p>
                          <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>{formatAmt(d.value)}</p>
                        </div>
                      );
                    })}
                </div>
              )}
            </section>

            {/* ── Section 4: Factory Performance + Top Buyers ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Factory Performance */}
              <section className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#f3f4f6', backgroundColor: '#fef3c7' }}>
                  <div className="flex items-center gap-2">
                    <Icon name="Factory" size={16} color="#92400e" />
                    <h2 className="text-sm font-bold" style={{ color: '#92400e' }}>Factory Performance</h2>
                  </div>
                  <span className="text-xs" style={{ color: '#92400e' }}>{presetLabel}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ backgroundColor: '#fffbf5' }}>
                        {['Factory','Listed','Booked','Util%'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.factoryPerf.length === 0 ? (
                        <tr><td colSpan={4} className="px-3 py-6 text-center" style={{ color: '#9ca3af' }}>No data for selected period</td></tr>
                      ) : analytics.factoryPerf.map((f, idx) => (
                        <tr key={f.name} className="border-t" style={{ borderColor: '#f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fffbf5' }}>
                          <td className="px-3 py-2.5 font-medium" style={{ color: '#1f2937', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</td>
                          <td className="px-3 py-2.5" style={{ color: '#374151' }}>{formatMT(f.listed)}</td>
                          <td className="px-3 py-2.5 font-semibold" style={{ color: '#065f46' }}>{formatMT(f.booked)}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: '#e5e7eb', minWidth: '40px' }}>
                                <div className="h-1.5 rounded-full" style={{ width: `${Math.min(f.util, 100)}%`, backgroundColor: f.util >= 80 ? '#059669' : f.util >= 50 ? '#f59e0b' : '#dc2626' }} />
                              </div>
                              <span className="font-semibold" style={{ color: f.util >= 80 ? '#059669' : f.util >= 50 ? '#92400e' : '#dc2626' }}>{f.util}%</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Top Buyers */}
              <section className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#f3f4f6', backgroundColor: '#d1fae5' }}>
                  <div className="flex items-center gap-2">
                    <Icon name="TrendingUp" size={16} color="#065f46" />
                    <h2 className="text-sm font-bold" style={{ color: '#065f46' }}>Top Buyers by Volume</h2>
                  </div>
                  <span className="text-xs" style={{ color: '#065f46' }}>{presetLabel}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ backgroundColor: '#f0fdf4' }}>
                        {['#','Buyer','MT','Value','Orders'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.topBuyers.length === 0 ? (
                        <tr><td colSpan={5} className="px-3 py-6 text-center" style={{ color: '#9ca3af' }}>No data for selected period</td></tr>
                      ) : analytics.topBuyers.map((b, idx) => (
                        <tr key={b.email} className="border-t" style={{ borderColor: '#f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#f0fdf4' }}>
                          <td className="px-3 py-2.5">
                            <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#92400e' : '#e5e7eb', color: idx < 3 ? '#fff' : '#374151' }}>
                              {idx + 1}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-medium" style={{ color: '#1f2937', maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.email}</td>
                          <td className="px-3 py-2.5 font-semibold" style={{ color: '#065f46' }}>{formatMT(b.mt)}</td>
                          <td className="px-3 py-2.5" style={{ color: '#374151' }}>{formatAmt(b.value)}</td>
                          <td className="px-3 py-2.5" style={{ color: '#6b7280' }}>{b.orders}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            {/* ── Section 5: Quota Health + Monthly Revenue ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Quota Health — always all-time */}
              <section className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="BarChart2" size={16} color="#7c3aed" />
                  <h2 className="text-sm font-bold" style={{ color: '#1f2937' }}>Quota Health <span className="text-xs font-normal" style={{ color: '#9ca3af' }}>(All Time)</span></h2>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Total Listed',  value: formatMT(analytics.totalListedMT),  color: '#1f2937' },
                    { label: 'Total Booked',  value: formatMT(analytics.totalBookedMT),  color: '#065f46' },
                    { label: 'Available Now', value: formatMT(analytics.totalAvailMT),   color: '#1d4ed8' },
                  ].map(row => (
                    <div key={row.label} className="flex justify-between items-center">
                      <span className="text-xs" style={{ color: '#6b7280' }}>{row.label}</span>
                      <span className="text-sm font-bold" style={{ color: row.color }}>{row.value}</span>
                    </div>
                  ))}
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs" style={{ color: '#6b7280' }}>Sold %</span>
                      <span className="text-xs font-bold" style={{ color: analytics.soldPct >= 70 ? '#059669' : '#f59e0b' }}>{analytics.soldPct}%</span>
                    </div>
                    <div className="h-2 rounded-full" style={{ backgroundColor: '#e5e7eb' }}>
                      <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(analytics.soldPct, 100)}%`, backgroundColor: analytics.soldPct >= 70 ? '#059669' : '#f59e0b' }} />
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: '#f3f4f6' }}>
                    <span className="text-xs" style={{ color: '#6b7280' }}>Slow Movers (&gt;7 days unsold)</span>
                    <span className="text-sm font-bold" style={{ color: analytics.slowMovers > 0 ? '#dc2626' : '#059669' }}>
                      {analytics.slowMovers} {analytics.slowMovers === 1 ? 'quota' : 'quotas'}
                    </span>
                  </div>
                </div>
              </section>

              {/* Monthly Revenue */}
              <section className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: '#f3f4f6', backgroundColor: '#dbeafe' }}>
                  <div className="flex items-center gap-2">
                    <Icon name="TrendingUp" size={16} color="#1e4a72" />
                    <h2 className="text-sm font-bold" style={{ color: '#1e4a72' }}>Monthly Revenue</h2>
                  </div>
                  <span className="text-xs" style={{ color: '#1e4a72' }}>{presetLabel}</span>
                </div>
                {analytics.monthlyRevenue.length === 0 ? (
                  <div className="p-6 text-center text-xs" style={{ color: '#9ca3af' }}>No data for selected period</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ backgroundColor: '#eff6ff' }}>
                        {['Month','Orders','MT','Value'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-semibold" style={{ color: '#6b7280' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...analytics.monthlyRevenue].reverse().map((m, idx) => (
                        <tr key={m.label} className="border-t" style={{ borderColor: '#f3f4f6', backgroundColor: idx === 0 ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                          <td className="px-3 py-2.5 font-semibold" style={{ color: idx === 0 ? '#1e4a72' : '#374151' }}>{m.label}</td>
                          <td className="px-3 py-2.5" style={{ color: '#374151' }}>{m.count}</td>
                          <td className="px-3 py-2.5" style={{ color: '#374151' }}>{formatMT(m.mt)}</td>
                          <td className="px-3 py-2.5 font-semibold" style={{ color: '#065f46' }}>{formatAmt(m.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </div>

            {/* ── Section 6: Detail Tabs ── */}
            <section>
              <div className="flex gap-2 flex-wrap mb-4">
                {tabs.map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition"
                    style={{ backgroundColor: activeTab === tab.key ? '#1e4a72' : '#fff', color: activeTab === tab.key ? '#fff' : '#374151', borderColor: activeTab === tab.key ? '#1e4a72' : '#e5e7eb' }}>
                    <Icon name={tab.icon} size={14} />
                    {tab.label}
                    <span className="px-1.5 py-0.5 rounded-full text-xs" style={{ backgroundColor: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#f3f4f6', color: activeTab === tab.key ? '#fff' : '#6b7280' }}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Orders Detail */}
              {activeTab === 'orders' && (
                filteredOrders.length === 0 ? (
                  <div className="bg-white rounded-2xl border shadow-sm p-12 text-center" style={{ borderColor: '#e5e7eb' }}>
                    <Icon name="ShoppingCart" size={32} color="#9ca3af" />
                    <p className="mt-3 font-semibold" style={{ color: '#374151' }}>No orders in selected period</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden lg:block bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr style={{ backgroundColor: '#fef3c7' }}>
                              {['Booking ID','Buyer Email','Factory','Grade','Qty (MT)','Rate/Ton','Total','Status','Date'].map(h => (
                                <th key={h} className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide whitespace-nowrap" style={{ color: '#92400e' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredOrders.map((o, idx) => {
                              const sc = statusBadge(o.status);
                              return (
                                <tr key={o.readable_booking_id || idx} className="border-t hover:bg-amber-50/30 transition-colors" style={{ borderColor: '#f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fffbf5' }}>
                                  <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap" style={{ color: '#1e4a72' }}>{o.readable_booking_id || '—'}</td>
                                  <td className="px-4 py-3 text-xs" style={{ color: '#374151', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.buyer_email || '—'}</td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#374151' }}>{o.factory_name || '—'}</td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#374151' }}>{o.grade || '—'}</td>
                                  <td className="px-4 py-3 text-xs font-semibold whitespace-nowrap" style={{ color: '#1f2937' }}>{o.quantity ? parseFloat(o.quantity).toLocaleString('en-IN') : '—'}</td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#374151' }}>{o.rate_per_ton ? formatAmt(o.rate_per_ton) : '—'}</td>
                                  <td className="px-4 py-3 text-xs font-semibold whitespace-nowrap" style={{ color: '#065f46' }}>{formatAmt(o.total_amount)}</td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ backgroundColor: sc.bg, color: sc.color }}>{(o.status || '—').replace(/_/g, ' ')}</span>
                                  </td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#6b7280' }}>{formatDate(o.booking_date || o.created_at)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="lg:hidden space-y-3">
                      {filteredOrders.map((o, idx) => {
                        const sc = statusBadge(o.status);
                        return (
                          <div key={o.readable_booking_id || idx} className="bg-white rounded-xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                            <div className="flex items-start justify-between mb-2">
                              <p className="font-mono text-xs font-bold" style={{ color: '#1e4a72' }}>{o.readable_booking_id || '—'}</p>
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ backgroundColor: sc.bg, color: sc.color }}>{(o.status || '—').replace(/_/g, ' ')}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5 text-xs" style={{ color: '#6b7280' }}>
                              <div><span className="font-medium">Factory:</span> {o.factory_name || '—'}</div>
                              <div><span className="font-medium">Grade:</span> {o.grade || '—'}</div>
                              <div><span className="font-medium">Qty:</span> {o.quantity ? parseFloat(o.quantity).toLocaleString('en-IN') + ' MT' : '—'}</div>
                              <div><span className="font-medium">Amount:</span> {formatAmt(o.total_amount)}</div>
                              <div className="col-span-2"><span className="font-medium">Buyer:</span> {o.buyer_email || '—'}</div>
                              <div className="col-span-2"><span className="font-medium">Date:</span> {formatDate(o.booking_date || o.created_at)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )
              )}

              {/* Quotas Detail */}
              {activeTab === 'quotas' && (
                filteredQuotas.length === 0 ? (
                  <div className="bg-white rounded-2xl border shadow-sm p-12 text-center" style={{ borderColor: '#e5e7eb' }}>
                    <Icon name="Package" size={32} color="#9ca3af" />
                    <p className="mt-3 font-semibold" style={{ color: '#374151' }}>No quotas in selected period</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden lg:block bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr style={{ backgroundColor: '#fef3c7' }}>
                              {['Offer ID','Factory','Grade','Qty (MT)','Rate/Ton','Status','Available From','Listed On'].map(h => (
                                <th key={h} className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide whitespace-nowrap" style={{ color: '#92400e' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredQuotas.map((q, idx) => {
                              const qc = q.availability_status === 'available' ? { bg: '#d1fae5', color: '#065f46' } : q.availability_status === 'sold_out' ? { bg: '#fee2e2', color: '#991b1b' } : { bg: '#fef3c7', color: '#92400e' };
                              return (
                                <tr key={q.readable_offer_id || idx} className="border-t hover:bg-amber-50/30 transition-colors" style={{ borderColor: '#f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fffbf5' }}>
                                  <td className="px-4 py-3 font-mono text-xs font-semibold whitespace-nowrap" style={{ color: '#1e4a72' }}>{q.readable_offer_id || '—'}</td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#374151' }}>{q.factory_name || '—'}</td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#374151' }}>{q.grade || '—'}</td>
                                  <td className="px-4 py-3 text-xs font-semibold whitespace-nowrap" style={{ color: '#1f2937' }}>{q.quantity ? parseFloat(q.quantity).toLocaleString('en-IN') : '—'}</td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#374151' }}>{q.price_per_ton ? formatAmt(q.price_per_ton) : '—'}</td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ backgroundColor: qc.bg, color: qc.color }}>{(q.availability_status || '—').replace(/_/g, ' ')}</span>
                                  </td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#6b7280' }}>{q.available_from_date || '—'}</td>
                                  <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#6b7280' }}>{formatDate(q.created_at)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="lg:hidden space-y-3">
                      {filteredQuotas.map((q, idx) => {
                        const qc = q.availability_status === 'available' ? { bg: '#d1fae5', color: '#065f46' } : { bg: '#fee2e2', color: '#991b1b' };
                        return (
                          <div key={q.readable_offer_id || idx} className="bg-white rounded-xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                            <div className="flex items-start justify-between mb-2">
                              <p className="font-mono text-xs font-bold" style={{ color: '#1e4a72' }}>{q.readable_offer_id || '—'}</p>
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize" style={{ backgroundColor: qc.bg, color: qc.color }}>{(q.availability_status || '—').replace(/_/g, ' ')}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5 text-xs" style={{ color: '#6b7280' }}>
                              <div><span className="font-medium">Factory:</span> {q.factory_name || '—'}</div>
                              <div><span className="font-medium">Grade:</span> {q.grade || '—'}</div>
                              <div><span className="font-medium">Qty:</span> {q.quantity ? parseFloat(q.quantity).toLocaleString('en-IN') + ' MT' : '—'}</div>
                              <div><span className="font-medium">Rate:</span> {q.price_per_ton ? formatAmt(q.price_per_ton) + '/ton' : '—'}</div>
                              <div><span className="font-medium">From:</span> {q.available_from_date || '—'}</div>
                              <div><span className="font-medium">Listed:</span> {formatDate(q.created_at)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )
              )}

              {/* Buyers Detail */}
              {activeTab === 'buyers' && (
                filteredBuyers.length === 0 ? (
                  <div className="bg-white rounded-2xl border shadow-sm p-12 text-center" style={{ borderColor: '#e5e7eb' }}>
                    <Icon name="Users" size={32} color="#9ca3af" />
                    <p className="mt-3 font-semibold" style={{ color: '#374151' }}>No buyers registered in selected period</p>
                  </div>
                ) : (
                  <>
                    <div className="hidden lg:block bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr style={{ backgroundColor: '#fef3c7' }}>
                              {['Buyer Name','Email','Company','GSTIN','City','Status','Registered'].map(h => (
                                <th key={h} className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide whitespace-nowrap" style={{ color: '#92400e' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBuyers.map((b, idx) => (
                              <tr key={b.email || idx} className="border-t hover:bg-amber-50/30 transition-colors" style={{ borderColor: '#f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fffbf5' }}>
                                <td className="px-4 py-3 font-medium whitespace-nowrap" style={{ color: '#1f2937' }}>
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: '#1e4a72' }}>{b.full_name?.charAt(0)?.toUpperCase() || '?'}</div>
                                    {b.full_name || '—'}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-xs" style={{ color: '#374151', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.email || '—'}</td>
                                <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#374151' }}>{b.company_name || '—'}</td>
                                <td className="px-4 py-3 text-xs font-mono whitespace-nowrap" style={{ color: '#374151' }}>{b.gstin || '—'}</td>
                                <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#374151' }}>{b.city || '—'}</td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: b.is_active ? '#d1fae5' : '#fee2e2', color: b.is_active ? '#065f46' : '#991b1b' }}>{b.is_active ? 'Active' : 'Inactive'}</span>
                                </td>
                                <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#6b7280' }}>{formatDate(b.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="lg:hidden space-y-3">
                      {filteredBuyers.map((b, idx) => (
                        <div key={b.email || idx} className="bg-white rounded-xl border shadow-sm p-4" style={{ borderColor: '#e5e7eb' }}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ backgroundColor: '#1e4a72' }}>{b.full_name?.charAt(0)?.toUpperCase() || '?'}</div>
                              <div>
                                <p className="font-semibold text-sm" style={{ color: '#1f2937' }}>{b.full_name || '—'}</p>
                                <p className="text-xs" style={{ color: '#9ca3af' }}>{b.company_name || '—'}</p>
                              </div>
                            </div>
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: b.is_active ? '#d1fae5' : '#fee2e2', color: b.is_active ? '#065f46' : '#991b1b' }}>{b.is_active ? 'Active' : 'Inactive'}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-1.5 text-xs" style={{ color: '#6b7280' }}>
                            <div className="col-span-2"><span className="font-medium">Email:</span> {b.email || '—'}</div>
                            <div><span className="font-medium">GSTIN:</span> {b.gstin || '—'}</div>
                            <div><span className="font-medium">City:</span> {b.city || '—'}</div>
                            <div><span className="font-medium">Registered:</span> {formatDate(b.created_at)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )
              )}
            </section>

            {/* Read-only notice */}
            <div className="flex items-center justify-center gap-2 py-3 text-xs" style={{ color: '#9ca3af' }}>
              <Icon name="Lock" size={12} />
              Read-only view — no edits permitted
            </div>

          </main>
        )}
      </div>
    </>
  );
};

export default MonitorDashboard;
